import OpenInNew from "@mui/icons-material/OpenInNew";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { GridRowId } from "@mui/x-data-grid-premium";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { getStatusLabel } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  SubmissionMaterializationType,
  SubmissionStatus,
  SubmissionType,
} from "generated-graphql/graphql";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { WASTE_SUBMISSIONS_FOR_STAFF_PICKER_QUERY } from "routes/Staff/WasteFulfillment/api";

import { mapWasteSubmissionRowsToCommunicationSubmissions } from "../hooks/useCommunicationSubmissions";
import { CommunicationSubmission } from "../types";

interface SubmissionsPickerDialogProps {
  tenantId: string;
  initialSubmissionIds: string[];
  close: () => void;
  saveSubmissions: (submissions: CommunicationSubmission[]) => void;
}

type WasteRow = {
  id: string;
  submissionId?: string | null;
  title: string | null;
  submissionType?: string | null;
  submissionStatus: string;
  tenantId: string;
  facilityName?: string | null;
  state?: string | null;
  facilities?: { id: string; name: string; state?: string | null }[] | null;
};

// Picker spans further back than the staff dashboard so already-filed submissions
// (resends, proof requests, corrections, penalty notices — sometimes 1-2 years
// after the filing deadline) remain selectable. Forward window matches the
// dashboard so in-flight items still appear.
const PICKER_LOOKBACK_YEARS = 2;
const PICKER_FORWARD_DAYS = 90;

// "facilityName" is the column field but not a key the staff-submission
// omnisearch keyMap understands; expose the real "facility" key instead
// (same exclude-and-re-add pattern as WasteFulfillment/columns.tsx).
const ADDITIONAL_FILTER_COLUMNS: FilterKey[] = [
  { header: "Facility", key: "facility", filterKeyType: "facility" },
];

function getPickerDateRange() {
  const startOfToday = DateTime.now().startOf("day");
  const start = startOfToday.minus({ years: PICKER_LOOKBACK_YEARS }).startOf("day");
  const end = startOfToday.plus({ days: PICKER_FORWARD_DAYS }).endOf("day");
  return {
    startDate: start.toISO() ?? "",
    endDate: end.toISO() ?? "",
  };
}

export const SubmissionsPickerDialog = ({
  tenantId,
  initialSubmissionIds,
  close,
  saveSubmissions,
}: SubmissionsPickerDialogProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSubmissionIds);
  const [selectedRows, setSelectedRows] = useState<WasteRow[]>([]);

  const pickerDateRange = useMemo(() => getPickerDateRange(), []);

  const dataQueryVariables = useMemo(
    () =>
      tenantId
        ? {
            materializationTypes: [
              SubmissionMaterializationType.OneOff,
              SubmissionMaterializationType.Materialized,
            ],
            includeOverdue: true,
            onlyOverdue: false,
            startDate: pickerDateRange.startDate,
            endDate: pickerDateRange.endDate,
          }
        : undefined,
    [tenantId, pickerDateRange.startDate, pickerDateRange.endDate]
  );

  const getItems = useCallback(
    (data: { wasteSubmissionsForStaffPicker: { items: WasteRow[] } }) => {
      const rows = data.wasteSubmissionsForStaffPicker.items as WasteRow[];
      return rows.map((row) => ({
        ...row,
        id: row.submissionId ?? row.id,
      }));
    },
    []
  );

  const getCount = useCallback(
    (data: { wasteSubmissionsForStaffPicker: { count: number } }) =>
      data.wasteSubmissionsForStaffPicker.count,
    []
  );

  const handleSelectedRowsChanged = useCallback((rows: WasteRow[]) => {
    setSelectedRows(rows);
  }, []);

  const handleSelectedIdsChanged = useCallback((ids: GridRowId[]) => {
    setSelectedIds(ids.map(String));
  }, []);

  const columns: OmnisearchGridColDef<WasteRow>[] = useMemo(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: 1.5,
        minWidth: 200,
      },
      // Never displayed (hidden via columnVisibilityModel, and the picker has no
      // Manage Columns). Exists so the deadline-desc initialSortModel survives —
      // the DataGrid drops sort entries whose field has no column definition.
      {
        field: "deadline",
        headerName: "Deadline",
        filterable: false,
        hideable: false,
      },
      {
        field: "facilityName",
        headerName: "Facility",
        flex: 1,
        minWidth: 160,
        sortable: true,
        renderCell: ({ row }) => {
          const facilities = row.facilities ?? [];
          const primary = facilities[0];
          const name = row.facilityName ?? primary?.name ?? "—";
          const state = row.state ?? primary?.state ?? "";
          const extraCount = Math.max(facilities.length - 1, 0);
          const tooltipLines =
            facilities.length > 1
              ? facilities.map((f) => ({
                  key: f.id,
                  text: `${f.name}${f.state ? ` (${f.state})` : ""}`,
                }))
              : [{ key: "name", text: name }, ...(state ? [{ key: "state", text: state }] : [])];
          return (
            <Tooltip
              title={
                <>
                  {tooltipLines.map((line) => (
                    <div key={line.key}>{line.text}</div>
                  ))}
                </>
              }
            >
              <Typography noWrap variant="body2">
                {name}
                {extraCount > 0 ? ` (+${extraCount} more)` : ""}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "submissionType",
        headerName: "Type",
        flex: 0.8,
        minWidth: 120,
        valueFormatter: (value) => (value != null ? prettyPrintEnumValue(String(value)) : ""),
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(SubmissionType),
      },
      {
        field: "submissionStatus",
        headerName: "Status",
        flex: 0.8,
        minWidth: 120,
        valueFormatter: (value) => (value != null ? getStatusLabel(String(value)) : ""),
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(SubmissionStatus),
        enumPresentationFunction: getStatusLabel,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.5,
        minWidth: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const submissionId = params.row.submissionId;
          if (!submissionId) return null;
          return (
            <Tooltip title="View submission">
              <Link
                to={`/staff/fulfillment/waste/submissions/${submissionId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Link>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  const handleDone = () => {
    const rowById = new Map(selectedRows.map((r) => [r.submissionId ?? r.id, r]));
    const submissions: CommunicationSubmission[] = selectedIds.map((id) => {
      const row = rowById.get(id);
      if (row) {
        const mapped = mapWasteSubmissionRowsToCommunicationSubmissions([row]);
        if (mapped[0]) return mapped[0];
      }
      return {
        id,
        title: "",
        submissionType: SubmissionType.AnnualReport,
        status: SubmissionStatus.NotStarted,
      };
    });
    saveSubmissions(submissions);
    close();
  };

  return (
    <Dialog
      open
      maxWidth="lg"
      onClose={close}
      title="Associate Submissions"
      description="Select waste submissions to associate with this communication. Includes in-progress submissions and submissions filed within the last 2 years. Only materialized submissions (one-off or from a series) are shown."
      primaryActionButtonProps={{
        onClick: handleDone,
        saveText: "Done",
        hideStartIcon: true,
        loading: false,
      }}
    >
      <OmnisearchDataGrid
        tenantId={tenantId}
        persistenceKey="communications-submissions-picker"
        columns={columns}
        dataQuery={WASTE_SUBMISSIONS_FOR_STAFF_PICKER_QUERY}
        skip={!tenantId}
        dataQueryVariables={dataQueryVariables}
        defaultSearch={`tenantId:${tenantId}`}
        initialSortModel={[{ field: "deadline", sort: "desc" }]}
        initialColumnVisibilityModel={{ deadline: false }}
        getItems={getItems}
        getCount={getCount}
        noDataMessage="No submissions found for this tenant."
        isURLDriven={false}
        persistOmnisearch={false}
        persistSortModel={false}
        selectedIds={selectedIds}
        disableRowSelectionOnClick={false}
        withPadding={false}
        initialPageSize={10}
        onSelectedIdsChanged={handleSelectedIdsChanged}
        onSelectedRowsChanged={handleSelectedRowsChanged}
        showManageColumns={false}
        excludeFilterColumns={["facilityName"]}
        additionalFilterColumns={ADDITIONAL_FILTER_COLUMNS}
      />
    </Dialog>
  );
};
